// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

// ** Components
import { Icon } from '@/components/common/icons';

// ** Types
import type React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import type { EventDetailTextSection } from '@/features/event-detail/blocks/event-detail-block/EventDetailBlock.types';

export type InfoCardDetail = {
	icon: string;
	label: string;
};

export type InfoCardAction = {
	label: string;
	priceLabel: React.ReactNode;
	variant?: 'dark' | 'light';
	onClick?: () => void;
	href?: string;
};

export type InfoCardProps = {
	title: string;
	subtitle?: string;
	details: InfoCardDetail[];
	actions?: InfoCardAction[];
	notes?: EventDetailTextSection[];
	/** Optional custom content rendered below the details instead of the default actions section. */
	bottomContent?: React.ReactNode;
	sx?: SxProps<Theme>;
	location?: string;
};

const cardPx = { xs: '1rem', md: '2rem' };
const cardPy = { xs: '1.5rem', md: '2rem' };

const InfoCard = ({ title, subtitle, details, actions, notes, bottomContent, sx }: InfoCardProps) => {
	const validNotes =
		notes?.filter((note) => {
			if (note.copy === undefined || note.copy === null) return false;

			if (typeof note.copy === 'string') {
				return note.copy.trim() !== '';
			}

			return true;
		}) || [];

	return (
		<Box
			sx={[
				(theme: Theme) => ({
					bgcolor: theme.palette.heroLightBackground.main,
					borderRadius: '0.5rem',
					display: 'flex',
					flexDirection: 'column'
				}),
				...(Array.isArray(sx) ? sx : sx ? [sx] : [])
			]}
		>
			{/* Title + subtitle */}
			<Box sx={{ px: cardPx, pt: cardPy }}>
				<Typography
					variant="title-md"
					sx={(t) => ({
						color: t.palette.text.primary,
						fontWeight: 500,
						fontSize: '1.125rem',
						lineHeight: '100%',
						letterSpacing: '0.05px',
						[t.breakpoints.up('md')]: {
							fontSize: '1.5rem',
							lineHeight: '2rem',
							letterSpacing: '-0.05px'
						}
					})}
				>
					{title}
				</Typography>
				{subtitle && (
					<Typography
						variant="body-md"
						sx={(t) => ({
							color: t.palette.text.primary,
							mt: '0.25rem',
							fontSize: '1.125rem',
							lineHeight: '100%',
							letterSpacing: '0.05px',
							[t.breakpoints.up('md')]: {
								fontSize: '1.5rem',
								lineHeight: '2rem',
								letterSpacing: '-0.05px'
							}
						})}
					>
						{subtitle}
					</Typography>
				)}
			</Box>

			{/* Detail rows */}
			<Box
				sx={{
					px: cardPx,
					pt: { xs: '1rem', md: '1.5rem' },
					pb: (actions && actions.length > 0) || bottomContent ? 0 : cardPy,
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem'
				}}
			>
				{details.map((detail) => (
					<Box key={`${detail.icon}-${detail.label}`} sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
						<Box
							sx={(theme) => ({
								color: theme.palette.primary.main,
								flexShrink: 0,
								display: 'flex',
								alignItems: 'center'
							})}
						>
							<Icon name={detail.icon} width={20} height={20} />
						</Box>
						<Typography
							variant="body-md"
							sx={(t) => ({
								color: t.palette.text.primary,
								fontSize: '1rem',
								lineHeight: '1.375rem',
								letterSpacing: '-0.05%',
								[t.breakpoints.up('md')]: {
									fontSize: '1.125rem',
									lineHeight: '1.625rem'
								}
							})}
						>
							{detail.label}
						</Typography>
					</Box>
				))}
			</Box>

			{/* Actions */}
			{actions && actions.length > 0 && (
				<Box sx={{ mt: { xs: '0.5rem', md: '1rem' } }}>
					<Box
						sx={{
							px: cardPx,
							pt: '1.5rem',
							pb: cardPy,
							display: 'flex',
							flexDirection: 'column',
							gap: { xs: '1.5rem', md: '1rem' }
						}}
					>
						{actions.map((action) => (
							<Box
								key={`${action.label}-${action.href ?? ''}-${action.variant ?? ''}`}
								sx={{
									display: 'flex',
									flexDirection: { xs: 'column', md: 'row' },
									alignItems: { xs: 'flex-start', md: 'center' },
									rowGap: '0.75rem',
									columnGap: '1rem'
								}}
							>
								<Button
									variant="contained"
									disableElevation
									href={action.href}
									onClick={action.onClick}
									sx={(theme) => ({
										bgcolor: theme.palette.grey[600],
										color: theme.palette.common.white,
										borderRadius: '0.25rem',
										textTransform: 'none',
										fontFamily: '"DIN Next LT Pro"',
										fontSize: '0.875rem',
										lineHeight: '1.25rem',
										fontWeight: 400,
										px: '2rem',
										py: '0.5rem',
										width: '160px',
										whiteSpace: 'nowrap',
										'&:hover': {
											bgcolor: theme.palette.grey[700]
										}
									})}
								>
									{action.label}
								</Button>
								<Typography
									variant="body-sm"
									component="span"
									sx={(t) => ({
										color: t.palette.text.primary,
										fontSize: '1rem',
										lineHeight: '1.5rem',
										letterSpacing: '-0.05px',
										[t.breakpoints.up('md')]: {
											fontSize: 'inherit',
											lineHeight: 'inherit',
											letterSpacing: 'inherit'
										}
									})}
								>
									{action.priceLabel}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>
			)}

			{/* Notes */}
			{validNotes.length > 0 && (
				<Box
					sx={{
						px: cardPx,
						pb: cardPy,
						display: 'flex',
						flexDirection: 'column',
						gap: { xs: '1.5rem', md: '1rem' }
					}}
				>
					{validNotes.map((note) => (
						<Box key={note.id} aria-labelledby={`event-detail-text-${note.id}`}>
							<Typography id={`event-detail-text-${note.id}`} component="h2" variant="title-sm" color="primary">
								{note.title}
							</Typography>

							{typeof note.copy === 'string' ? (
								<Typography variant="body-md" component="div" sx={{ color: 'text.secondary' }}>
									{note.copy}
								</Typography>
							) : (
								<Box
									sx={{
										typography: 'body-md',
										color: 'text.secondary'
									}}
								>
									{note.copy}
								</Box>
							)}
						</Box>
					))}
				</Box>
			)}

			{/* Custom bottom content */}
			{bottomContent}
		</Box>
	);
};

export default InfoCard;
